import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { inputsMaxLengths } from '../../../../../constants';
import styles from './FormContent.module.scss';
import { Button, Input, Text } from 'components';
import { appActions } from 'store/app/slice';
import { usersActions } from 'store/users/slice';
import { useDispatch } from 'store/utils';
import { User, UserStatus } from 'types';

const schema = yup.object({
  username: yup.string().required('The field is empty'),
  email: yup.string().email('The text is not an email').required('The field is empty'),
});

type Props = {
  user: User;
  onClose(): void;
  setEmailUpdated(value: boolean): void;
  setEmail(value: string): void;
  setDisplayError(value: boolean): void;
};

export const FormContent = ({ user, onClose, setEmailUpdated, setEmail, setDisplayError }: Props) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const isEmailInputDisabled = user.status === UserStatus.PENDING;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, dirtyFields },
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      username: user.name,
      email: user.email,
    },
  });

  const watchedEmail = watch('email');
  const watchedUsername = watch('username');

  useEffect(() => {
    setEmail(watchedEmail);
  }, [setEmail, watchedEmail]);

  const onSubmit = handleSubmit(async (formData) => {
    if (isLoading) return;

    const userDataPayload = isEmailInputDisabled
      ? {
          name: formData.username,
        }
      : {
          name: formData.username,
          email: formData.email,
        };
    try {
      setIsLoading(true);
      const wrappedResult = dispatch(
        usersActions.updateUser({
          userData: userDataPayload,
          userID: user.id,
        }),
      );
      const { isUpdated } = await wrappedResult.unwrap();
      if (isUpdated) {
        !dirtyFields['email'] ? onClose() : setEmailUpdated(true);
      }
      dispatch(appActions.setModalErrorText({ name: 'editUserError', errorText: '' }));
    } catch (error: any) {
      dispatch(appActions.setModalErrorText({ name: 'editUserError', errorText: error?.title }));
      setDisplayError(true);
    } finally {
      setIsLoading(false);
    }
  });

  return (
    <div className={styles.formContainer}>
      <form className={styles.form}>
        <Input
          inputName="username"
          labelText="Name of the user"
          placeholder="Enter user name"
          register={register}
          registerOptions={{ required: true, maxLength: inputsMaxLengths.username }}
          maxLength={inputsMaxLengths.username}
          autoComplete="off"
          currentValue={watchedUsername}
          setValue={setValue}
          errorMsg={errors.username?.message}
          characterCounter
        />
        <div className={styles.emailField}>
          <Input
            inputName="email"
            labelText="Email"
            placeholder="Enter user email"
            register={register}
            registerOptions={{ required: true }}
            errorMsg={errors.email?.message}
            disabled={isEmailInputDisabled}
          />
          {!isEmailInputDisabled && (
            <Text className={styles.emailWarning} type="caption">
              Please be aware that user will need to update the password with new email address
            </Text>
          )}
        </div>
      </form>
      <div className={styles.buttonRow}>
        <Button isLink onClick={onClose}>
          Cancel Editing
        </Button>
        <Button onClick={onSubmit}>Save changes</Button>
      </div>
    </div>
  );
};
